
import { useState, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'

interface Insumo {
  _id: string
  nome: string
  tipo: 'material' | 'mao_obra' | 'equipamento'
  unidadeMedida: string
  precoUnitario: number
  ativo: boolean
  observacoes?: string
  creator?: string
  createdAt?: string
  updatedAt?: string
}

interface Composicao {
  _id: string
  nome: string
  unidadeMedida: string
  itens: Array<{
    itemId: string
    tipo: 'insumo' | 'composicao'
    quantidade: number
    precoUnitario: number
    subtotal: number
  }>
  valorTotal: number
  observacoes?: string
  ativo: boolean
  creator?: string
  createdAt?: string
  updatedAt?: string
}

interface HistoricoPreco {
  _id: string
  insumoId: string
  precoAnterior: number
  precoNovo: number
  percentualVariacao: number
  motivo: string
  dataAlteracao: string
  usuario: string
  creator?: string
  createdAt?: string
}

export const useInsumos = () => {
  const [insumos, setInsumos] = useState<Insumo[]>([])
  const [composicoes, setComposicoes] = useState<Composicao[]>([])
  const [historicoPrecos, setHistoricoPrecos] = useState<HistoricoPreco[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 🔥 FUNÇÃO AUXILIAR: Converter para número com segurança
  const toSafeNumber = (value: any): number => {
    if (value === null || value === undefined || value === '') return 0
    const num = Number(value)
    return isNaN(num) ? 0 : num
  }

  // 🔥 FUNÇÃO AUXILIAR: Normalizar dados do insumo
  const normalizeInsumo = (insumo: any): Insumo => {
    return {
      ...insumo,
      precoUnitario: toSafeNumber(insumo.precoUnitario),
      ativo: Boolean(insumo.ativo !== false) // Default true se não especificado
    }
  }

  // 🆕 NOVA FUNCIONALIDADE: Atualizar preços em cascata
  const atualizarPrecosEmCascata = async (insumoId: string, novoPreco: number, nomeInsumo: string) => {
    try {
      console.log(`🔄 Iniciando atualização em cascata para insumo: ${nomeInsumo} (${insumoId})`)
      console.log(`💰 Novo preço: R$ ${novoPreco.toFixed(2)}`)
      
      let composicoesAtualizadas = 0
      let orcamentosAtualizados = 0

      // 1️⃣ ATUALIZAR COMPOSIÇÕES
      console.log('📋 Buscando composições que usam este insumo...')
      
      const composicoesAfetadas = composicoes.filter(comp => 
        comp.itens?.some(item => item.itemId === insumoId && item.tipo === 'insumo')
      )

      console.log(`📋 Encontradas ${composicoesAfetadas.length} composições afetadas`)

      for (const composicao of composicoesAfetadas) {
        try {
          console.log(`  🔧 Atualizando composição: ${composicao.nome}`)
          
          // Atualizar itens da composição
          const itensAtualizados = composicao.itens.map(item => {
            if (item.itemId === insumoId && item.tipo === 'insumo') {
              const novoSubtotal = item.quantidade * novoPreco
              console.log(`    💡 Item atualizado: ${item.quantidade} × R$ ${novoPreco.toFixed(2)} = R$ ${novoSubtotal.toFixed(2)}`)
              
              return {
                ...item,
                precoUnitario: novoPreco,
                subtotal: novoSubtotal
              }
            }
            return item
          })

          // Recalcular valor total da composição
          const novoValorTotal = itensAtualizados.reduce((total, item) => total + item.subtotal, 0)
          
          console.log(`    💰 Valor total atualizado: R$ ${composicao.valorTotal.toFixed(2)} → R$ ${novoValorTotal.toFixed(2)}`)

          // Atualizar composição no banco
          await lumi.entities.composicoes.update(composicao._id, {
            itens: itensAtualizados,
            valorTotal: novoValorTotal,
            updatedAt: new Date().toISOString()
          })

          composicoesAtualizadas++
          console.log(`    ✅ Composição "${composicao.nome}" atualizada`)

        } catch (error) {
          console.error(`    ❌ Erro ao atualizar composição "${composicao.nome}":`, error)
        }
      }

      // 2️⃣ ATUALIZAR ORÇAMENTOS (exceto fechados)
      console.log('📊 Buscando orçamentos que usam este insumo...')
      
      try {
        const orcamentosData = await lumi.entities.orcamentos.list()
        const orcamentos = orcamentosData.list || []
        
        const orcamentosAfetados = orcamentos.filter(orc => 
          orc.status !== 'fechado' && // 🔥 EXCLUIR ORÇAMENTOS FECHADOS
          orc.etapas?.some(etapa => 
            etapa.subetapas?.some(subetapa =>
              subetapa.itens?.some(item => 
                item.itemId === insumoId && item.tipo === 'insumo'
              )
            )
          )
        )

        console.log(`📊 Encontrados ${orcamentosAfetados.length} orçamentos afetados (excluindo fechados)`)

        for (const orcamento of orcamentosAfetados) {
          try {
            console.log(`  📋 Atualizando orçamento: ${orcamento.numero} (${orcamento.status})`)
            
            let valorSubtotalAtualizado = 0
            
            // Atualizar etapas do orçamento
            const etapasAtualizadas = orcamento.etapas?.map(etapa => ({
              ...etapa,
              subetapas: etapa.subetapas?.map(subetapa => ({
                ...subetapa,
                itens: subetapa.itens?.map(item => {
                  if (item.itemId === insumoId && item.tipo === 'insumo') {
                    const novoSubtotal = item.quantidade * novoPreco
                    console.log(`      💡 Item atualizado: ${item.nome} - ${item.quantidade} × R$ ${novoPreco.toFixed(2)} = R$ ${novoSubtotal.toFixed(2)}`)
                    
                    valorSubtotalAtualizado += novoSubtotal
                    
                    return {
                      ...item,
                      precoUnitario: novoPreco,
                      subtotal: novoSubtotal
                    }
                  }
                  valorSubtotalAtualizado += item.subtotal
                  return item
                }) || []
              })) || []
            })) || []

            // Recalcular valor total com BDI e desconto
            const bdi = toSafeNumber(orcamento.bdi)
            const desconto = toSafeNumber(orcamento.desconto)
            const valorComBDI = valorSubtotalAtualizado * (1 + bdi / 100)
            const valorTotal = valorComBDI * (1 - desconto / 100)

            console.log(`    💰 Valores atualizados:`)
            console.log(`      - Subtotal: R$ ${orcamento.valorSubtotal?.toFixed(2)} → R$ ${valorSubtotalAtualizado.toFixed(2)}`)
            console.log(`      - Total: R$ ${orcamento.valorTotal?.toFixed(2)} → R$ ${valorTotal.toFixed(2)}`)

            // Atualizar orçamento no banco
            await lumi.entities.orcamentos.update(orcamento._id, {
              etapas: etapasAtualizadas,
              valorSubtotal: valorSubtotalAtualizado,
              valorTotal: valorTotal,
              updatedAt: new Date().toISOString()
            })

            // Criar entrada no histórico do orçamento
            await lumi.entities.historico_orcamentos.create({
              orcamentoId: orcamento._id,
              acao: 'preco_atualizado',
              descricao: `Preço do insumo "${nomeInsumo}" atualizado automaticamente`,
              valorAnterior: orcamento.valorTotal,
              valorNovo: valorTotal,
              dataAcao: new Date().toISOString(),
              usuario: 'Sistema - Atualização Automática',
              observacoes: `Insumo: ${nomeInsumo} | Novo preço: R$ ${novoPreco.toFixed(2)}`,
              creator: 'sistema',
              createdAt: new Date().toISOString()
            })

            orcamentosAtualizados++
            console.log(`    ✅ Orçamento "${orcamento.numero}" atualizado`)

          } catch (error) {
            console.error(`    ❌ Erro ao atualizar orçamento "${orcamento.numero}":`, error)
          }
        }

      } catch (error) {
        console.error('❌ Erro ao buscar orçamentos:', error)
      }

      // 3️⃣ RELATÓRIO FINAL
      console.log(`\n🎉 ATUALIZAÇÃO EM CASCATA CONCLUÍDA:`)
      console.log(`   📋 Composições atualizadas: ${composicoesAtualizadas}`)
      console.log(`   📊 Orçamentos atualizados: ${orcamentosAtualizados}`)
      
      if (composicoesAtualizadas > 0 || orcamentosAtualizados > 0) {
        const mensagem = `Preço atualizado em cascata: ${composicoesAtualizadas} composições e ${orcamentosAtualizados} orçamentos atualizados automaticamente`
        toast.success(mensagem, { duration: 5000 })
        
        // Criar notificação no sistema
        try {
          await lumi.entities.notificacoes.create({
            tipo: 'sistema',
            titulo: 'Preços Atualizados Automaticamente',
            mensagem,
            lida: false,
            dataNotificacao: new Date().toISOString(),
            usuario: 'admin',
            creator: 'sistema',
            createdAt: new Date().toISOString()
          })
        } catch (notifError) {
          console.warn('Erro ao criar notificação:', notifError)
        }
      }

    } catch (error) {
      console.error('❌ Erro na atualização em cascata:', error)
      toast.error('Erro na atualização automática de preços')
    }
  }

  // 🔥 CORREÇÃO: Fetch data com melhor sincronização
  const fetchData = useCallback(async (forceRefresh = false) => {
    setLoading(true)
    setError(null)
    
    try {
      console.log('🔄 Recarregando dados dos insumos...', forceRefresh ? '(forçado)' : '')
      
      // Fetch data with proper error handling for each entity
      const fetchWithFallback = async (entityName: string, entityMethod: any) => {
        try {
          const result = await entityMethod()
          console.log(`✅ ${entityName} carregado:`, result?.list?.length || result?.length || 0, 'itens')
          return result?.list || result || []
        } catch (err) {
          console.warn(`❌ Erro ao carregar ${entityName}:`, err)
          return []
        }
      }

      const [insumosData, composicoesData, historicoData] = await Promise.allSettled([
        fetchWithFallback('insumos', () => lumi.entities.insumos.list()),
        fetchWithFallback('composicoes', () => lumi.entities.composicoes.list()),
        fetchWithFallback('historico_precos', () => lumi.entities.historico_precos.list())
      ])

      // Process results with safety checks
      const processResult = (result: any) => {
        if (result.status === 'fulfilled') {
          const data = result.value
          return Array.isArray(data) ? data : (data?.list || [])
        }
        return []
      }

      // 🔥 NORMALIZAÇÃO: Aplicar normalização aos insumos
      const rawInsumos = processResult(insumosData)
      const normalizedInsumos = rawInsumos.map(normalizeInsumo)

      const processedComposicoes = processResult(composicoesData)
      const processedHistorico = processResult(historicoData)

      console.log('📊 Dados processados:')
      console.log('  - Insumos:', normalizedInsumos.length)
      console.log('  - Composições:', processedComposicoes.length)
      console.log('  - Histórico:', processedHistorico.length)

      setInsumos(normalizedInsumos)
      setComposicoes(processedComposicoes)
      setHistoricoPrecos(processedHistorico)

    } catch (error: any) {
      console.error('❌ Erro crítico ao carregar dados:', error)
      setError('Erro ao carregar dados do sistema')
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  // Calculate metrics with enhanced safety checks
  const getMetrics = useCallback(() => {
    try {
      // Verificar se insumos existe e é um array válido
      if (!Array.isArray(insumos) || insumos.length === 0) {
        return {
          totalInsumos: 0,
          valorMedioInsumos: 0,
          insumosAtivos: 0,
          produtoMaiorPreco: 'N/A'
        }
      }

      const totalInsumos = insumos.length
      const insumosAtivos = insumos.filter(i => i && i.ativo).length
      
      const somaPrecos = insumos.reduce((total, item) => {
        const preco = toSafeNumber(item?.precoUnitario)
        return total + preco
      }, 0)
      
      const valorMedioInsumos = totalInsumos > 0 ? somaPrecos / totalInsumos : 0

      // Produto com maior preço
      const insumoMaiorPreco = insumos.reduce((maior, atual) => {
        const precoAtual = toSafeNumber(atual?.precoUnitario)
        const precoMaior = toSafeNumber(maior?.precoUnitario)
        return precoAtual > precoMaior ? atual : maior
      }, insumos[0])

      const produtoMaiorPreco = insumoMaiorPreco?.nome || 'N/A'

      return {
        totalInsumos,
        valorMedioInsumos,
        insumosAtivos,
        produtoMaiorPreco
      }
    } catch (error) {
      console.error('Erro ao calcular métricas:', error)
      return {
        totalInsumos: 0,
        valorMedioInsumos: 0,
        insumosAtivos: 0,
        produtoMaiorPreco: 'N/A'
      }
    }
  }, [insumos])

  // Create insumo with enhanced validation
  const createInsumo = async (data: Omit<Insumo, '_id'>) => {
    try {
      // Validação de dados
      if (!data.nome || !data.tipo || !data.unidadeMedida) {
        throw new Error('Campos obrigatórios não preenchidos')
      }

      const insumoData = {
        nome: String(data.nome).trim(),
        tipo: data.tipo,
        unidadeMedida: String(data.unidadeMedida).trim(),
        precoUnitario: toSafeNumber(data.precoUnitario),
        ativo: true,
        observacoes: data.observacoes || '',
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.insumos.create(insumoData)
      await fetchData(true) // 🔥 CORREÇÃO: Forçar recarregamento
      toast.success('Insumo criado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao criar insumo:', error)
      toast.error(error.message || 'Erro ao criar insumo')
      throw error
    }
  }

  // 🔥 CORREÇÃO PRINCIPAL: Update insumo com atualização em cascata
  const updateInsumo = async (id: string, data: Partial<Insumo>) => {
    try {
      if (!id) throw new Error('ID do insumo é obrigatório')

      const insumoAtual = insumos.find(i => i?._id === id)
      if (!insumoAtual) throw new Error('Insumo não encontrado')

      // 🔥 NORMALIZAÇÃO: Aplicar normalização aos dados de entrada
      const normalizedData = {
        ...data,
        precoUnitario: data.precoUnitario !== undefined ? toSafeNumber(data.precoUnitario) : undefined
      }

      // 🆕 NOVA FUNCIONALIDADE: Verificar se o preço mudou para atualização em cascata
      const precoMudou = normalizedData.precoUnitario !== undefined && 
                         normalizedData.precoUnitario !== toSafeNumber(insumoAtual.precoUnitario)

      // Se o preço mudou, criar histórico
      if (precoMudou) {
        const precoAnterior = toSafeNumber(insumoAtual.precoUnitario)
        const precoNovo = toSafeNumber(normalizedData.precoUnitario)
        const percentualVariacao = precoAnterior > 0 
          ? ((precoNovo - precoAnterior) / precoAnterior) * 100 
          : 0

        try {
          await lumi.entities.historico_precos.create({
            insumoId: id,
            precoAnterior,
            precoNovo,
            percentualVariacao: Number(percentualVariacao.toFixed(2)),
            motivo: data.observacoes || 'Alteração de preço',
            dataAlteracao: new Date().toISOString(),
            usuario: 'Admin Sistema',
            creator: 'admin',
            createdAt: new Date().toISOString()
          })
        } catch (historicoError) {
          console.warn('Erro ao criar histórico de preço:', historicoError)
          // Não bloquear a atualização do insumo por erro no histórico
        }
      }

      const updateData = {
        ...normalizedData,
        updatedAt: new Date().toISOString()
      }

      // Atualizar o insumo primeiro
      await lumi.entities.insumos.update(id, updateData)

      // 🆕 EXECUTAR ATUALIZAÇÃO EM CASCATA se o preço mudou
      if (precoMudou) {
        console.log(`🔄 Preço do insumo "${insumoAtual.nome}" alterado. Iniciando atualização em cascata...`)
        await atualizarPrecosEmCascata(id, toSafeNumber(normalizedData.precoUnitario), insumoAtual.nome)
      }

      await fetchData(true) // 🔥 CORREÇÃO: Forçar recarregamento
      toast.success('Insumo atualizado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar insumo:', error)
      toast.error(error.message || 'Erro ao atualizar insumo')
      throw error
    }
  }

  // Delete insumo with dependency check
  const deleteInsumo = async (id: string) => {
    try {
      if (!id) throw new Error('ID do insumo é obrigatório')

      // Verificar se o insumo está sendo usado em composições
      const composicoesUsando = composicoes.filter(c => 
        c?.itens?.some(item => item?.itemId === id && item?.tipo === 'insumo')
      )

      if (composicoesUsando.length > 0) {
        const nomes = composicoesUsando.map(c => c.nome).join(', ')
        throw new Error(`Não é possível excluir. Insumo está sendo usado nas composições: ${nomes}`)
      }

      await lumi.entities.insumos.delete(id)
      await fetchData(true) // 🔥 CORREÇÃO: Forçar recarregamento
      toast.success('Insumo excluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir insumo:', error)
      toast.error(error.message || 'Erro ao excluir insumo')
      throw error
    }
  }

  // 🆕 NOVA FUNCIONALIDADE: Excluir insumos em massa
  const deleteInsumosBulk = async (ids: string[]) => {
    try {
      if (!ids || ids.length === 0) {
        throw new Error('Nenhum insumo selecionado para exclusão')
      }

      // Verificar dependências para todos os insumos selecionados
      const insumosComDependencias: string[] = []
      
      for (const id of ids) {
        const composicoesUsando = composicoes.filter(c => 
          c?.itens?.some(item => item?.itemId === id && item?.tipo === 'insumo')
        )
        
        if (composicoesUsando.length > 0) {
          const insumo = insumos.find(i => i._id === id)
          if (insumo) {
            insumosComDependencias.push(insumo.nome)
          }
        }
      }

      if (insumosComDependencias.length > 0) {
        throw new Error(`Os seguintes insumos não podem ser excluídos pois estão sendo usados em composições: ${insumosComDependencias.join(', ')}`)
      }

      // Excluir todos os insumos em paralelo
      const deletePromises = ids.map(id => lumi.entities.insumos.delete(id))
      await Promise.all(deletePromises)

      await fetchData(true)
      toast.success(`${ids.length} insumo(s) excluído(s) com sucesso!`)
    } catch (error: any) {
      console.error('Erro ao excluir insumos em massa:', error)
      toast.error(error.message || 'Erro ao excluir insumos em massa')
      throw error
    }
  }

  // Create composição with validation
  const createComposicao = async (data: Omit<Composicao, '_id'>) => {
    try {
      if (!data.nome || !data.unidadeMedida || !data.itens || data.itens.length === 0) {
        throw new Error('Campos obrigatórios não preenchidos')
      }

      // Validar itens da composição
      for (const item of data.itens) {
        if (item.tipo === 'insumo') {
          const insumo = insumos.find(i => i._id === item.itemId)
          if (!insumo) {
            throw new Error(`Insumo não encontrado: ${item.itemId}`)
          }
        } else if (item.tipo === 'composicao') {
          const composicao = composicoes.find(c => c._id === item.itemId)
          if (!composicao) {
            throw new Error(`Composição não encontrada: ${item.itemId}`)
          }
        }
      }

      // 🔥 NORMALIZAÇÃO: Aplicar normalização aos itens da composição
      const normalizedItens = data.itens.map(item => ({
        ...item,
        quantidade: toSafeNumber(item.quantidade),
        precoUnitario: toSafeNumber(item.precoUnitario),
        subtotal: toSafeNumber(item.subtotal)
      }))

      const composicaoData = {
        nome: String(data.nome).trim(),
        unidadeMedida: String(data.unidadeMedida).trim(),
        itens: normalizedItens,
        valorTotal: toSafeNumber(data.valorTotal),
        observacoes: data.observacoes || '',
        ativo: true,
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.composicoes.create(composicaoData)
      await fetchData(true) // 🔥 CORREÇÃO: Forçar recarregamento
      toast.success('Composição criada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao criar composição:', error)
      toast.error(error.message || 'Erro ao criar composição')
      throw error
    }
  }

  // Update composição
  const updateComposicao = async (id: string, data: Partial<Composicao>) => {
    try {
      if (!id) throw new Error('ID da composição é obrigatório')

      // 🔥 NORMALIZAÇÃO: Aplicar normalização se itens foram alterados
      const normalizedData = { ...data }
      if (data.itens) {
        normalizedData.itens = data.itens.map(item => ({
          ...item,
          quantidade: toSafeNumber(item.quantidade),
          precoUnitario: toSafeNumber(item.precoUnitario),
          subtotal: toSafeNumber(item.subtotal)
        }))
      }
      if (data.valorTotal !== undefined) {
        normalizedData.valorTotal = toSafeNumber(data.valorTotal)
      }

      const updateData = {
        ...normalizedData,
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.composicoes.update(id, updateData)
      await fetchData(true) // 🔥 CORREÇÃO: Forçar recarregamento
      toast.success('Composição atualizada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar composição:', error)
      toast.error(error.message || 'Erro ao atualizar composição')
      throw error
    }
  }

  // Delete composição
  const deleteComposicao = async (id: string) => {
    try {
      if (!id) throw new Error('ID da composição é obrigatório')

      // Verificar se a composição está sendo usada em outras composições
      const composicoesUsando = composicoes.filter(c => 
        c?.itens?.some(item => item?.itemId === id && item?.tipo === 'composicao')
      )

      if (composicoesUsando.length > 0) {
        const nomes = composicoesUsando.map(c => c.nome).join(', ')
        throw new Error(`Não é possível excluir. Composição está sendo usada em: ${nomes}`)
      }

      await lumi.entities.composicoes.delete(id)
      await fetchData(true) // 🔥 CORREÇÃO: Forçar recarregamento
      toast.success('Composição excluída com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir composição:', error)
      toast.error(error.message || 'Erro ao excluir composição')
      throw error
    }
  }

  // Initialize data on mount
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    insumos,
    composicoes,
    historicoPrecos,
    loading,
    error,
    metrics: getMetrics(),
    fetchData,
    createInsumo,
    updateInsumo,
    deleteInsumo,
    deleteInsumosBulk, // 🆕 Nova função para exclusão em massa
    createComposicao,
    updateComposicao,
    deleteComposicao,
    atualizarPrecosEmCascata // 🆕 Função para atualização em cascata
  }
}
